import { useMemo, useRef, useState } from "react";
import type { MealEntry, MealType, NutritionEstimate, ParsedFoodItem } from "@jilian/shared";
import { useMeals, useSettings } from "../db/hooks";
import { saveMeal, deleteMeal, saveMealPrefCorrection, loadMealPrefs } from "../db/repo";
import { requestMealEstimate, type EstimateOutcome } from "../lib/sync/aiClient";
import { localDateString } from "../lib/timezone";
import { uuid } from "../lib/ids";
import { Button, Card, ProgressBar, RiskTag, SectionTitle, Segmented } from "../components/ui";

const MEAL_LABEL: Record<MealType, string> = {
  breakfast: "早餐",
  lunch: "午餐",
  dinner: "晚餐",
  snack: "加餐",
};

const QUICK_PRESETS = [
  { label: "+500 kcal 标准正餐", calories: 500, protein: 30, fat: 15, carbs: 60, mealType: "lunch" as MealType, text: "标准正餐" },
  { label: "+150 kcal 蛋白粉 1 勺", calories: 150, protein: 25, fat: 2, carbs: 5, mealType: "snack" as MealType, text: "蛋白粉 1 勺" },
  { label: "+350 kcal 减脂餐", calories: 350, protein: 35, fat: 8, carbs: 35, mealType: "lunch" as MealType, text: "鸡胸减脂餐" },
  { label: "+150 kcal 水果轻食", calories: 150, protein: 2, fat: 1, carbs: 35, mealType: "snack" as MealType, text: "水果加餐" },
  { label: "+750 kcal 聚餐大餐", calories: 750, protein: 35, fat: 35, carbs: 75, mealType: "dinner" as MealType, text: "聚餐大餐" },
];

interface DraftMeal {
  mealType: MealType;
  rawText: string;
  estimate: NutritionEstimate | null;
  final: { calories: number; protein: number; fat: number; carbs: number };
  userCorrected: boolean;
  status: "idle" | "parsing" | "estimated" | "failed";
  /** 降级/失败原因文案（如实展示，不伪造估算） */
  failReason: string | null;
  /** 估算来源（provider/model，服务端返回后展示） */
  source: string | null;
}

const EMPTY_DRAFT: DraftMeal = {
  mealType: "lunch",
  rawText: "",
  estimate: null,
  final: { calories: 0, protein: 0, fat: 0, carbs: 0 },
  userCorrected: false,
  status: "idle",
  failReason: null,
  source: null,
};

export function Diet() {
  const settings = useSettings();
  const userId = settings?.profile?.id ?? "user-default";
  const nutrition = settings?.nutrition ?? null;
  const meals = useMeals(userId) ?? [];
  const [draft, setDraft] = useState<DraftMeal>(EMPTY_DRAFT);
  // 餐食 UUID：随草稿生成一次，服务端以此幂等（重复请求不重复计费）
  const mealIdRef = useRef<string>(uuid());

  const totals = useMemo(() => {
    const acc = meals.reduce(
      (a, m) => ({
        calories: a.calories + (m.finalCalories ?? 0),
        protein: a.protein + (m.finalProtein ?? 0),
        fat: a.fat + (m.finalFat ?? 0),
        carbs: a.carbs + (m.finalCarbs ?? 0),
      }),
      { calories: 0, protein: 0, fat: 0, carbs: 0 },
    );
    return acc;
  }, [meals]);
  const t = nutrition ?? { calories: 2200, proteinGrams: 140, fatGrams: 60, carbsGrams: 275 };

  const setFinal = (key: keyof DraftMeal["final"], v: number) =>
    setDraft((d) => ({ ...d, final: { ...d.final, [key]: v }, userCorrected: true }));

  const applyOutcome = (outcome: EstimateOutcome) => {
    if (outcome.ok) {
      const est = outcome.estimate;
      setDraft((d) => ({
        ...d,
        estimate: est,
        final: {
          calories: est.caloriesMid,
          protein: est.proteinMid,
          fat: est.fatMid,
          carbs: est.carbsMid,
        },
        userCorrected: false,
        status: "estimated",
        failReason: null,
        source: `${outcome.provider}/${outcome.model}${outcome.cached ? "（缓存）" : ""}`,
      }));
      return;
    }
    const reasonByStatus: Record<string, string> = {
      not_configured: "AI 服务未配置：本次记录保留原文并标记「待估算」，使用下方本地快速录入（客户端不伪造估算）。",
      unauthorized: "尚未登录：登录后即可使用 AI 估算；当前可先用本地快速录入。",
      budget_exceeded: "今日 AI 预算已用尽：已保留原文并标记「待估算」，使用下方本地快速录入，明日额度恢复。",
      count_exceeded: "今日 AI 解析次数已达上限：已保留原文并标记「待估算」，使用下方本地快速录入。",
      schema_invalid: "AI 返回结构不合法（已丢弃，不计入）：请重试或使用本地快速录入。",
      network: "网络不可用：已保留原文并标记「待估算」，联网后重试或本地录入。",
      server_error: "估算服务暂时不可用：已保留原文并标记「待估算」。",
    };
    setDraft((d) => ({ ...d, status: "failed", failReason: reasonByStatus[outcome.status] ?? outcome.message }));
  };

  const estimate = async () => {
    if (!draft.rawText.trim()) return;
    setDraft((d) => ({ ...d, status: "parsing", failReason: null }));
    try {
      const prefs = await loadMealPrefs(10);
      const outcome = await requestMealEstimate({
        mealId: mealIdRef.current,
        rawText: draft.rawText,
        mealType: draft.mealType,
        clientCreatedAt: new Date().toISOString(),
        prefs,
        localDate: localDateString(),
      });
      applyOutcome(outcome);
    } catch {
      applyOutcome({ ok: false, status: "network", message: "请求失败" });
    }
  };

  const addPreset = (p: (typeof QUICK_PRESETS)[0]) => {
    const entry: MealEntry = {
      id: uuid(),
      userId,
      mealType: p.mealType,
      consumedAt: new Date().toISOString(),
      rawText: p.text,
      processingStatus: "estimated",
      finalCalories: p.calories,
      finalProtein: p.protein,
      finalFat: p.fat,
      finalCarbs: p.carbs,
      userCorrected: false,
      deviceId: "device-demo-1",
      syncStatus: "local_only",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    void saveMeal(entry);
  };

  const confirm = () => {
    const entry: MealEntry = {
      id: mealIdRef.current,
      userId,
      mealType: draft.mealType,
      consumedAt: new Date().toISOString(),
      rawText: draft.rawText,
      // 阶段 5：估算成功 → estimated/corrected；AI 不可用 → pending_estimate（联网后补处理）
      processingStatus: draft.userCorrected ? "corrected" : draft.estimate ? "estimated" : "pending_estimate",
      finalCalories: draft.final.calories,
      finalProtein: draft.final.protein,
      finalFat: draft.final.fat,
      finalCarbs: draft.final.carbs,
      userCorrected: draft.userCorrected,
      deviceId: "device-demo-1",
      syncStatus: "local_only",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    void saveMeal(entry); // 即时落库；列表由 liveQuery 自动更新
    // 用户修正 → 形成个人偏好（§18.2/§32.3 #8）
    if (draft.userCorrected) {
      void saveMealPrefCorrection(entry);
    }
    mealIdRef.current = uuid();
    setDraft(EMPTY_DRAFT);
  };

  return (
    <div className="space-y-4 pb-12">
      <header className="pt-2">
        <h1 className="text-[26px] font-extrabold text-tone-ink tracking-tight">饮食管理</h1>
        <p className="text-xs font-semibold text-tone-soft mt-0.5">主抓热量赤字/盈余与核心蛋白质摄入</p>
      </header>

      {/* 核心营养看板（主打热量与蛋白质） */}
      <Card className="p-4 space-y-3.5 shadow-sm">
        <div>
          <div className="flex items-center justify-between text-xs font-bold mb-1.5">
            <span className="text-tone-ink text-sm">今日总摄入热量</span>
            <span className="text-tone-soft tabular-nums">
              <span className="text-tone-ink font-extrabold text-base">{totals.calories}</span> / {t.calories} kcal
            </span>
          </div>
          <ProgressBar value={(totals.calories / t.calories) * 100} />
        </div>

        <div className="grid grid-cols-3 gap-2.5 pt-1 text-xs">
          <div className="p-2.5 rounded-xl bg-tone-good/10 flex flex-col justify-between">
            <span className="text-tone-good font-bold">🥩 蛋白质 (核心)</span>
            <div className="text-[16px] font-extrabold text-tone-ink mt-1 tabular-nums">
              {totals.protein} / {t.proteinGrams}g
            </div>
            <div className="h-1 w-full rounded-full bg-tone-good/20 mt-1.5 overflow-hidden">
              <div
                className="h-full bg-tone-good rounded-full"
                style={{ width: `${Math.min(100, (totals.protein / t.proteinGrams) * 100)}%` }}
              />
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-black/5 dark:bg-white/5 flex flex-col justify-between">
            <span className="text-tone-soft font-semibold">🥑 脂肪</span>
            <div className="text-[16px] font-extrabold text-tone-ink mt-1 tabular-nums">
              {totals.fat} / {t.fatGrams}g
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-black/5 dark:bg-white/5 flex flex-col justify-between">
            <span className="text-tone-soft font-semibold">🍚 碳水</span>
            <div className="text-[16px] font-extrabold text-tone-ink mt-1 tabular-nums">
              {totals.carbs} / {t.carbsGrams}g
            </div>
          </div>
        </div>
      </Card>

      {/* 记录一餐 */}
      <SectionTitle>记录一餐</SectionTitle>
      <Card className="p-4 space-y-3.5 mb-4">
        {/* 1-Tap 快捷闪电预设 */}
        <div>
          <div className="text-[12px] font-bold text-tone-soft mb-2">⚡ 1-Tap 常用快捷预设</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_PRESETS.map((p, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => addPreset(p)}
                className="px-3 py-1.5 rounded-xl bg-black/5 dark:bg-white/10 hover:bg-tone-accentSoft hover:text-tone-accent active:scale-95 transition-all text-xs font-bold text-tone-ink"
              >
                <span>{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-black/5 dark:border-white/5">
          <div className="text-[12px] font-bold text-tone-soft mb-1.5">餐次分类</div>
          <Segmented<MealType>
            value={draft.mealType}
            onChange={(v) => setDraft((d) => ({ ...d, mealType: v }))}
            options={(Object.keys(MEAL_LABEL) as MealType[]).map((t) => ({ value: t, label: MEAL_LABEL[t] }))}
          />
        </div>

        <div>
          <div className="text-[12px] font-bold text-tone-soft mb-1.5">一句话描述（支持 AI 智能识别）</div>
          <textarea
            value={draft.rawText}
            onChange={(e) => setDraft((d) => ({ ...d, rawText: e.target.value }))}
            placeholder="例如：一碗白米饭，200克煎鸡胸肉，一盘西蓝花，一勺蛋白粉"
            className="w-full min-h-[75px] px-3.5 py-2.5 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-black/5 dark:border-white/10 text-tone-ink text-sm font-medium focus:outline-none focus:ring-1 focus:ring-tone-accent"
          />
        </div>

        <Button
          className="w-full h-[46px] font-bold text-[15px]"
          onClick={estimate}
          disabled={!draft.rawText.trim() || draft.status === "parsing"}
        >
          {draft.status === "parsing" ? "DeepSeek 正在估算营养…" : "✨ AI 智能估算热量与营养"}
        </Button>

        {draft.status === "estimated" && draft.estimate ? (
          <div className="rounded-2xl border border-tone-good/30 bg-tone-good/[0.04] p-3.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiskTag tone="good">估算成功</RiskTag>
                <span className="text-[12px] font-bold text-tone-ink">
                  {draft.estimate.caloriesLow}–{draft.estimate.caloriesHigh} kcal
                </span>
              </div>
              <span className="text-[11px] font-semibold text-tone-soft">{draft.source ?? "DeepSeek"}</span>
            </div>

            {draft.estimate.parsedItems.length > 0 && (
              <div className="p-2 rounded-xl bg-white/60 dark:bg-white/5 space-y-1 text-xs">
                {draft.estimate.parsedItems.map((it: ParsedFoodItem, i: number) => (
                  <div key={i} className="flex items-center justify-between text-tone-ink font-medium">
                    <span>
                      {it.name} <span className="text-tone-soft font-normal">({it.portionText})</span>
                    </span>
                    <span className="font-bold tabular-nums">~{it.caloriesMid} kcal</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-4 gap-2 pt-1">
              {(["calories", "protein", "fat", "carbs"] as const).map((k) => (
                <div key={k} className="p-2 rounded-xl bg-black/5 dark:bg-white/5">
                  <div className="text-[10px] font-bold text-tone-soft">
                    {k === "calories" ? "热量(kcal)" : k === "protein" ? "蛋白(g)" : k === "fat" ? "脂肪(g)" : "碳水(g)"}
                  </div>
                  <input
                    type="number"
                    value={draft.final[k]}
                    onChange={(e) => setFinal(k, Number(e.target.value))}
                    className="w-full text-sm font-extrabold text-tone-ink bg-transparent focus:outline-none tabular-nums mt-0.5"
                  />
                </div>
              ))}
            </div>

            <Button className="w-full bg-tone-good font-bold" onClick={confirm} disabled={draft.final.calories <= 0}>
              ✓ 确认并保存记录
            </Button>
          </div>
        ) : null}

        {draft.status === "failed" ? (
          <div className="rounded-xl border border-tone-warn/40 bg-tone-warn/5 p-3 text-xs">
            <div className="font-bold text-tone-warn mb-1">估算提示</div>
            <p className="text-tone-ink">{draft.failReason ?? "AI 接口请求异常"}</p>
            <Button variant="secondary" className="w-full mt-2 font-bold" onClick={confirm} disabled={draft.final.calories <= 0}>
              手动输入数值确认
            </Button>
          </div>
        ) : null}
      </Card>

      {/* 今日已记录餐食列表 */}
      <SectionTitle hint={`${meals.length} 条记录`}>今日饮食明细</SectionTitle>
      <div className="space-y-3">
        {(["breakfast", "lunch", "dinner", "snack"] as MealType[]).map((type) => {
          const items = meals.filter((m) => m.mealType === type);
          const typeTotalKcal = items.reduce((s, i) => s + (i.finalCalories ?? 0), 0);

          return (
            <Card key={type} className="p-3.5">
              <div className="text-[14px] font-bold text-tone-ink mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <span>{type === "breakfast" ? "🌅" : type === "lunch" ? "☀️" : type === "dinner" ? "🌙" : "🍎"}</span>
                  <span>{MEAL_LABEL[type]}</span>
                </span>
                <span className="text-xs font-semibold text-tone-soft tabular-nums">
                  {items.length > 0 ? `${typeTotalKcal} kcal` : "暂无记录"}
                </span>
              </div>

              {items.length > 0 ? (
                <div className="space-y-1.5">
                  {items.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-2 rounded-xl bg-black/[0.02] dark:bg-white/[0.03] text-xs"
                    >
                      <div className="flex-1 pr-2">
                        <div className="text-tone-ink font-semibold">{m.rawText}</div>
                        <div className="text-tone-soft text-[11px] mt-0.5 flex items-center gap-2">
                          <span>蛋白 {m.finalProtein ?? 0}g</span>
                          <span>•</span>
                          <span>脂肪 {m.finalFat ?? 0}g</span>
                          <span>•</span>
                          <span>碳水 {m.finalCarbs ?? 0}g</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-tone-ink tabular-nums text-sm">
                          {m.finalCalories} kcal
                        </span>
                        <button
                          type="button"
                          onClick={() => void deleteMeal(m.id, userId)}
                          className="w-6 h-6 rounded-full hover:bg-black/10 dark:hover:bg-white/10 text-tone-soft hover:text-tone-danger flex items-center justify-center text-xs font-bold"
                          aria-label="删除此条"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[12px] text-tone-soft py-1">暂无该餐次记录</div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
